import { useMemo, useState } from "react";
import {
  DateFilter,
  DateFilterConfiguration,
  DateFilterState,
  DateString,
  EnumFilter,
  EnumFilterConfiguration,
  Filter,
  FilterConfiguration,
  NumberFilter,
  NumberFilterConfiguration,
  NumberFilterState,
  StringFilter,
  StringFilterConfiguration,
} from "../types";
import Button from "../../Button/Button";
import { ChevronLeft } from "../../Icon/Icon";
import Radio from "../../Radio/Radio";
import toTitleCase from "../../../util/toTitleCase";
import styles from "./ConfigureFilter.module.scss";
import DatePickerInput from "../../DatePickerInput/DatePickerInput";
import TextInput from "../../TextInput/TextInput";
import Select from "../../Select/Select";
import MultiSelect from "../../MultiSelect/MultiSelect";
import NumberInput from "../../NumberInput/NumberInput";
import dateStringLabels from "./date-string-labels";

function isValidDate(date: string): boolean {
  try {
    return !!new Date(date)?.toISOString();
  } catch {
    return false;
  }
}

function ConfigureDateFilter({
  options,
  onBack,
  onSave,
}: {
  options: DateFilterConfiguration;
  onBack: () => void;
  onSave: (filter: DateFilter) => void;
}) {
  const [filterState, setFilterState] = useState<DateFilterState>(
    options.initialState ?? {
      filterType: "IS",
      value: "TODAY",
    },
  );

  const data = useMemo(() => {
    return Object.entries(dateStringLabels).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, []);

  return (
    <div>
      <Button
        variant="outline"
        size="xsmall"
        leftIcon={<ChevronLeft />}
        onClick={onBack}
      >
        Back
      </Button>
      <h3 className={styles.configureFilterTitle}>{options.label}</h3>
      <div className={styles.configureFilterOption}>
        <label className={styles.configureFilterCheckboxLabel} htmlFor="IS">
          <Radio
            id="IS"
            name="filterType"
            value="IS"
            checked={filterState.filterType === "IS"}
            onChange={() =>
              setFilterState({ filterType: "IS", value: "TODAY" })
            }
          />
          Is
        </label>
        {filterState.filterType === "IS" && (
          <Select
            data={data}
            value={filterState.value}
            onChange={(value) =>
              setFilterState({
                filterType: "IS",
                value: value ? (value as DateString) : "TODAY",
              })
            }
          />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label className={styles.configureFilterCheckboxLabel} htmlFor="EQUALS">
          <Radio
            id="EQUALS"
            name="filterType"
            value="EQUALS"
            checked={filterState.filterType === "EQUALS"}
            onChange={() =>
              setFilterState((previousFilterState) => ({
                filterType: "EQUALS",
                value:
                  typeof previousFilterState.value === "string" &&
                  isValidDate(previousFilterState.value)
                    ? previousFilterState.value
                    : undefined,
              }))
            }
          />
          Equals
        </label>
        {filterState.filterType === "EQUALS" && (
          <DatePickerInput
            value={filterState.value ? new Date(filterState.value) : undefined}
            placeholder="Select date"
            onChange={(value) =>
              setFilterState({
                filterType: "EQUALS",
                value: value?.toISOString() ?? "",
              })
            }
          />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label className={styles.configureFilterCheckboxLabel} htmlFor="BEFORE">
          <Radio
            id="BEFORE"
            name="filterType"
            value="BEFORE"
            checked={filterState.filterType === "BEFORE"}
            onChange={() =>
              setFilterState((previousFilterState) => ({
                filterType: "BEFORE",
                value:
                  typeof previousFilterState.value === "string" &&
                  isValidDate(previousFilterState.value)
                    ? previousFilterState.value
                    : undefined,
              }))
            }
          />
          Before
        </label>
        {filterState.filterType === "BEFORE" && (
          <DatePickerInput
            value={filterState.value ? new Date(filterState.value) : undefined}
            placeholder="Select date"
            onChange={(value) =>
              setFilterState({
                filterType: "BEFORE",
                value: value?.toISOString() ?? "",
              })
            }
          />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label className={styles.configureFilterCheckboxLabel} htmlFor="AFTER">
          <Radio
            id="AFTER"
            name="filterType"
            value="AFTER"
            checked={filterState.filterType === "AFTER"}
            onChange={() =>
              setFilterState((previousFilterState) => ({
                filterType: "AFTER",
                value:
                  typeof previousFilterState.value === "string" &&
                  isValidDate(previousFilterState.value)
                    ? previousFilterState.value
                    : undefined,
              }))
            }
          />
          After
        </label>
        {filterState.filterType === "AFTER" && (
          <DatePickerInput
            value={filterState.value ? new Date(filterState.value) : undefined}
            placeholder="Select date"
            onChange={(value) =>
              setFilterState({
                filterType: "AFTER",
                value: value?.toISOString() ?? "",
              })
            }
          />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label
          className={styles.configureFilterCheckboxLabel}
          htmlFor="BETWEEN"
        >
          <Radio
            id="BETWEEN"
            name="filterType"
            value="BETWEEN"
            checked={filterState.filterType === "BETWEEN"}
            onChange={() =>
              setFilterState((previousFilterState) => ({
                filterType: "BETWEEN",
                value:
                  typeof previousFilterState.value === "string" &&
                  isValidDate(previousFilterState.value)
                    ? { from: previousFilterState.value, to: undefined }
                    : undefined,
              }))
            }
          />
          Between
        </label>
        {filterState.filterType === "BETWEEN" && (
          <div className={styles.datePickerRow}>
            <DatePickerInput
              value={
                filterState.value?.from
                  ? new Date(filterState.value.from)
                  : undefined
              }
              placeholder="From"
              maxDate={
                filterState.value?.to
                  ? new Date(filterState.value?.to)
                  : undefined
              }
              onChange={(value) =>
                setFilterState((previousFilterState) => ({
                  filterType: "BETWEEN",
                  value: {
                    to:
                      previousFilterState.filterType === "BETWEEN"
                        ? previousFilterState.value?.to
                        : undefined,
                    from: value?.toISOString() ?? undefined,
                  },
                }))
              }
            />
            <DatePickerInput
              value={
                filterState.value?.to
                  ? new Date(filterState.value.to)
                  : undefined
              }
              placeholder="To"
              minDate={
                filterState.value?.from
                  ? new Date(filterState.value?.from)
                  : undefined
              }
              onChange={(value) =>
                setFilterState((previousFilterState) => ({
                  filterType: "BETWEEN",
                  value: {
                    from:
                      previousFilterState.filterType === "BETWEEN"
                        ? previousFilterState.value?.from
                        : undefined,
                    to: value?.toISOString() ?? undefined,
                  },
                }))
              }
            />
          </div>
        )}
      </div>
      <Button
        onClick={() => {
          const { filterType, value } = filterState;

          if (!value) {
            return;
          }

          if (filterType === "BETWEEN") {
            const { to, from } = value;

            if (!to || !from) {
              return;
            }

            return onSave({
              type: "date",
              configuration: options,
              filterType,
              value: { to, from },
            });
          }

          if (filterType === "IS") {
            return onSave({
              type: "date",
              configuration: options,
              filterType,
              value,
            });
          }

          return onSave({
            type: "date",
            configuration: options,
            filterType,
            value,
          });
        }}
        disabled={
          filterState.filterType === "BETWEEN"
            ? !filterState.value?.to || !filterState.value?.from
            : !filterState.value
        }
        variant="outline"
        className={styles.emptyStateButton}
      >
        Apply filter
      </Button>
    </div>
  );
}

function ConfigureStringFilter({
  options,
  onBack,
  onSave,
}: {
  options: StringFilterConfiguration;
  onBack: () => void;
  onSave: (filter: StringFilter) => void;
}) {
  const [filterType, setFilterType] = useState<"CONTAINS" | "DOESNT_CONTAIN">(
    options.initialState?.filterType || "CONTAINS",
  );
  const [value, setValue] = useState<string>(options.initialState?.value || "");

  return (
    <div>
      <Button
        variant="outline"
        size="xsmall"
        leftIcon={<ChevronLeft />}
        onClick={onBack}
      >
        Back
      </Button>
      <h3 className={styles.configureFilterTitle}>{options.label}</h3>
      <div className={styles.configureFilterOption}>
        <label
          className={styles.configureFilterCheckboxLabel}
          htmlFor="CONTAINS"
        >
          <Radio
            id="CONTAINS"
            name="filterType"
            value="CONTAINS"
            checked={filterType === "CONTAINS"}
            onChange={() => setFilterType("CONTAINS")}
          />
          Contains
        </label>
        {filterType === "CONTAINS" && (
          <TextInput value={value} onChange={(e) => setValue(e.target.value)} />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label
          className={styles.configureFilterCheckboxLabel}
          htmlFor="DOESNT_CONTAIN"
        >
          <Radio
            id="DOESNT_CONTAIN"
            name="filterType"
            value="DOESNT_CONTAIN"
            checked={filterType === "DOESNT_CONTAIN"}
            onChange={() => setFilterType("DOESNT_CONTAIN")}
          />
          Doesn&apos;t contain
        </label>
        {filterType === "DOESNT_CONTAIN" && (
          <TextInput value={value} onChange={(e) => setValue(e.target.value)} />
        )}
      </div>
      <Button
        onClick={() =>
          onSave({
            type: "string",
            filterType,
            value,
            configuration: options,
          })
        }
        disabled={!value.length}
        variant="outline"
        className={styles.emptyStateButton}
      >
        Apply filter
      </Button>
    </div>
  );
}

function ConfigureNumberFilter({
  options,
  onBack,
  onSave,
}: {
  options: NumberFilterConfiguration;
  onBack: () => void;
  onSave: (filter: NumberFilter) => void;
}) {
  const [filterState, setFilterState] = useState<NumberFilterState>(
    options.initialState ?? {
      filterType: "EQUALS",
      value: 0,
    },
  );

  return (
    <div>
      <Button
        variant="outline"
        size="xsmall"
        leftIcon={<ChevronLeft />}
        onClick={onBack}
      >
        Back
      </Button>
      <h3 className={styles.configureFilterTitle}>{options.label}</h3>
      <div className={styles.configureFilterOption}>
        <label className={styles.configureFilterCheckboxLabel} htmlFor="EQUALS">
          <Radio
            id="EQUALS"
            name="filterType"
            value="EQUALS"
            checked={filterState.filterType === "EQUALS"}
            onChange={() =>
              setFilterState((previousState) => ({
                filterType: "EQUALS",
                value:
                  typeof previousState.value === "number"
                    ? previousState.value
                    : 0,
              }))
            }
          />
          Equals
        </label>
        {filterState.filterType === "EQUALS" && (
          <NumberInput
            value={filterState.value?.toString()}
            onChange={(e) =>
              setFilterState({
                filterType: "EQUALS",
                value: parseInt(e.target.value),
              })
            }
          />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label
          className={styles.configureFilterCheckboxLabel}
          htmlFor="GREATER_THAN"
        >
          <Radio
            id="GREATER_THAN"
            name="filterType"
            value="GREATER_THAN"
            checked={filterState.filterType === "GREATER_THAN"}
            onChange={() =>
              setFilterState((previousState) => ({
                filterType: "GREATER_THAN",
                value:
                  typeof previousState.value === "number"
                    ? previousState.value
                    : 0,
              }))
            }
          />
          Greater Than
        </label>
        {filterState.filterType === "GREATER_THAN" && (
          <NumberInput
            value={filterState.value?.toString()}
            onChange={(e) =>
              setFilterState({
                filterType: "GREATER_THAN",
                value: parseInt(e.target.value),
              })
            }
          />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label
          className={styles.configureFilterCheckboxLabel}
          htmlFor="LESS_THAN"
        >
          <Radio
            id="LESS_THAN"
            name="filterType"
            value="LESS_THAN"
            checked={filterState.filterType === "LESS_THAN"}
            onChange={() =>
              setFilterState((previousState) => ({
                filterType: "LESS_THAN",
                value:
                  typeof previousState.value === "number"
                    ? previousState.value
                    : 0,
              }))
            }
          />
          Less Than
        </label>
        {filterState.filterType === "LESS_THAN" && (
          <NumberInput
            value={filterState.value?.toString()}
            onChange={(e) =>
              setFilterState({
                filterType: "LESS_THAN",
                value: parseInt(e.target.value),
              })
            }
          />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label
          className={styles.configureFilterCheckboxLabel}
          htmlFor="BETWEEN"
        >
          <Radio
            id="BETWEEN"
            name="filterType"
            value="BETWEEN"
            checked={filterState.filterType === "BETWEEN"}
            onChange={() =>
              setFilterState((previousState) => ({
                filterType: "BETWEEN",
                value: {
                  from:
                    typeof previousState.value === "number"
                      ? previousState.value
                      : 0,
                  to:
                    typeof previousState.value === "number"
                      ? previousState.value
                      : 0,
                },
              }))
            }
          />
          Between
        </label>
        {filterState.filterType === "BETWEEN" && (
          <div className={styles.datePickerRow}>
            <NumberInput
              value={filterState.value?.from?.toString()}
              onChange={(e) =>
                setFilterState((previousState) => ({
                  filterType: "BETWEEN",
                  value: {
                    from: parseInt(e.target.value),
                    to:
                      previousState.filterType === "BETWEEN"
                        ? previousState.value?.to
                        : undefined,
                  },
                }))
              }
            />

            <NumberInput
              value={filterState.value?.to?.toString()}
              onChange={(e) =>
                setFilterState((previousState) => ({
                  filterType: "BETWEEN",
                  value: {
                    to: parseInt(e.target.value),
                    from:
                      previousState.filterType === "BETWEEN"
                        ? previousState.value?.from
                        : undefined,
                  },
                }))
              }
            />
          </div>
        )}
      </div>
      <Button
        onClick={() => {
          const { filterType, value } = filterState;

          if (!value) {
            return;
          }

          if (filterType === "BETWEEN") {
            const { to, from } = value;

            if (!to || !from) {
              return;
            }

            return onSave({
              type: "number",
              configuration: options,
              filterType,
              value: { to, from },
            });
          }

          return onSave({
            type: "number",
            configuration: options,
            filterType,
            value,
          });
        }}
        disabled={
          filterState.filterType === "BETWEEN"
            ? typeof filterState.value?.to !== "number" ||
              typeof filterState.value?.from !== "number"
            : typeof filterState.value !== "number"
        }
        variant="outline"
        className={styles.emptyStateButton}
      >
        Apply filter
      </Button>
    </div>
  );
}

function ConfigureEnumFilter({
  options,
  onBack,
  onSave,
}: {
  options: EnumFilterConfiguration;
  onBack: () => void;
  onSave: (filter: EnumFilter) => void;
}) {
  const [filterType, setFilterType] = useState<"IS_ANY_OF" | "IS_NONE_OF">(
    options.initialState?.filterType || "IS_ANY_OF",
  );
  const [values, setValues] = useState<{ value: string; label: string }[]>(
    options.initialState?.values || [],
  );
  const formattedValues = options.values.map((value) => ({
    label: toTitleCase(value.label),
    value: value.value,
  }));

  return (
    <div>
      <Button
        variant="outline"
        size="xsmall"
        leftIcon={<ChevronLeft />}
        onClick={onBack}
      >
        Back
      </Button>
      <h3 className={styles.configureFilterTitle}>{options.label}</h3>
      <div className={styles.configureFilterOption}>
        <label
          className={styles.configureFilterCheckboxLabel}
          htmlFor="isAnyOf"
        >
          <Radio
            id="isAnyOf"
            name="filterType"
            value="IS_ANY_OF"
            checked={filterType === "IS_ANY_OF"}
            onChange={() => setFilterType("IS_ANY_OF")}
          />
          is any of
        </label>
        {filterType === "IS_ANY_OF" && (
          <MultiSelect
            data={formattedValues}
            value={values.map((value) => value.value)}
            onChange={(v) =>
              setValues(
                formattedValues.filter((value) => v.includes(value.value)),
              )
            }
          />
        )}
      </div>
      <div className={styles.configureFilterOption}>
        <label
          className={styles.configureFilterCheckboxLabel}
          htmlFor="isNoneOf"
        >
          <Radio
            id="isNoneOf"
            name="filterType"
            value="IS_NONE_OF"
            checked={filterType === "IS_NONE_OF"}
            onChange={() => setFilterType("IS_NONE_OF")}
          />
          is none of
        </label>
        {filterType === "IS_NONE_OF" && (
          <MultiSelect
            data={formattedValues}
            value={values.map((value) => value.value)}
            onChange={(v) =>
              setValues(
                formattedValues.filter((value) => v.includes(value.value)),
              )
            }
          />
        )}
      </div>
      <Button
        onClick={() =>
          onSave({
            type: "enum",
            filterType,
            values,
            configuration: options,
          })
        }
        disabled={!values.length}
        variant="outline"
        className={styles.emptyStateButton}
      >
        Apply filter
      </Button>
    </div>
  );
}

function ConfigureFilter({
  options,
  onBack,
  onSave,
}: {
  options: FilterConfiguration;
  onBack: () => void;
  onSave: (filter: Filter) => void;
}) {
  if (options.type === "enum") {
    return (
      <ConfigureEnumFilter options={options} onBack={onBack} onSave={onSave} />
    );
  }

  if (options.type === "string") {
    return (
      <ConfigureStringFilter
        options={options}
        onBack={onBack}
        onSave={onSave}
      />
    );
  }

  if (options.type === "date") {
    return (
      <ConfigureDateFilter options={options} onBack={onBack} onSave={onSave} />
    );
  }

  if (options.type === "number") {
    return (
      <ConfigureNumberFilter
        options={options}
        onBack={onBack}
        onSave={onSave}
      />
    );
  }

  return (
    <Button
      variant="outline"
      size="xsmall"
      leftIcon={<ChevronLeft />}
      onClick={onBack}
    >
      Back
    </Button>
  );
}

export default ConfigureFilter;
